import { useEffect } from 'react';

function ElementPicker({ iframeRef, onElementPicked, onCancel }) {
  useEffect(() => {
    // Nothing to attach to until the iframe has loaded. Throwing here trips the
    // boundary and kills the whole chat panel, taking the buttons that could
    // recover it.
    const iframeWindow = iframeRef.current?.contentWindow;
    const iframeDocument = iframeWindow?.document;
    if (!iframeDocument?.body) {
      // Cancel rather than sit inert. Bailing quietly left the navbar telling
      // the user to click an element and press ESC, while nothing was listening
      // for either - and no other way out of picker mode.
      onCancel();
      return undefined;
    }

    // Create overlay for highlighting
    const overlay = iframeDocument.createElement('div');
    overlay.id = 'ai-element-picker-overlay';
    overlay.style.cssText = `
      position: absolute;
      pointer-events: none;
      border: 2px solid #2075ff;
      background: rgba(32, 117, 255, 0.1);
      z-index: 999998;
      transition: all 0.05s;
    `;
    iframeDocument.body.appendChild(overlay);

    // Create blocking overlay to prevent editor interactions
    const blockingOverlay = iframeDocument.createElement('div');
    blockingOverlay.id = 'ai-picker-blocking-overlay';
    blockingOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 999997;
      cursor: crosshair;
    `;
    iframeDocument.body.appendChild(blockingOverlay);

    // Mouse move handler - highlight element using elementFromPoint
    function handleMouseMove(e) {
      e.preventDefault();
      e.stopPropagation();

      // Get element at cursor position (like Chrome DevTools)
      const x = e.clientX;
      const y = e.clientY;

      // Temporarily hide overlays to get element underneath
      overlay.style.display = 'none';
      blockingOverlay.style.pointerEvents = 'none';

      const target = iframeDocument.elementFromPoint(x, y);

      overlay.style.display = 'block';
      blockingOverlay.style.pointerEvents = 'auto';

      // Skip document/html/body or our own elements
      if (
        !target ||
        target === iframeDocument.documentElement ||
        target === iframeDocument.body ||
        target.id === 'ai-element-picker-overlay' ||
        target.id === 'ai-picker-blocking-overlay' ||
        target.id === 'tiiny-style'
      ) {
        overlay.style.display = 'none';
        return;
      }

      // Position overlay over element
      const rect = target.getBoundingClientRect();
      const scrollTop = iframeWindow.pageYOffset || iframeDocument.documentElement.scrollTop;
      const scrollLeft = iframeWindow.pageXOffset || iframeDocument.documentElement.scrollLeft;

      overlay.style.top = `${rect.top + scrollTop}px`;
      overlay.style.left = `${rect.left + scrollLeft}px`;
      overlay.style.width = `${rect.width}px`;
      overlay.style.height = `${rect.height}px`;
      overlay.style.display = 'block';
    }

    // Click handler - select element
    function handleClick(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Get element at click position
      const x = e.clientX;
      const y = e.clientY;

      overlay.style.display = 'none';
      blockingOverlay.style.pointerEvents = 'none';
      const target = iframeDocument.elementFromPoint(x, y);
      blockingOverlay.style.pointerEvents = 'auto';

      if (!target || target === iframeDocument.documentElement || target === iframeDocument.body) {
        return;
      }

      // Generate selector
      const selector = generateSelector(target);
      const html = target.outerHTML;

      // Cleanup
      cleanup();

      // Return to parent
      onElementPicked(selector, html);
    }

    // Generate CSS selector for element
    function generateSelector(element) {
      if (element.id && !element.id.startsWith('tiiny')) {
        return `#${element.id}`;
      }

      const classes = Array.from(element.classList)
        .filter(c => !c.startsWith('tiiny'))
        .join('.');

      if (classes) {
        return `.${classes}`;
      }

      return element.tagName.toLowerCase();
    }

    // Prevent all editor interactions
    function blockEditorEvents(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }

    // Cleanup function
    function cleanup() {
      blockingOverlay.removeEventListener('mousemove', handleMouseMove, true);
      blockingOverlay.removeEventListener('click', handleClick, true);
      iframeDocument.removeEventListener('keydown', handleEscape, true);
      document.removeEventListener('keydown', handleEscape, true);

      // Remove blocking for all editor events
      iframeDocument.removeEventListener('mousedown', blockEditorEvents, true);
      iframeDocument.removeEventListener('mouseup', blockEditorEvents, true);
      iframeDocument.removeEventListener('mouseover', blockEditorEvents, true);
      iframeDocument.removeEventListener('mouseout', blockEditorEvents, true);

      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      if (blockingOverlay.parentNode) {
        blockingOverlay.parentNode.removeChild(blockingOverlay);
      }
    }

    // ESC key handler - cancel
    function handleEscape(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        cleanup();
        onCancel();
      }
    }

    // Attach event listeners with capture phase to intercept before editor
    blockingOverlay.addEventListener('mousemove', handleMouseMove, true);
    blockingOverlay.addEventListener('click', handleClick, true);
    iframeDocument.addEventListener('keydown', handleEscape, true);
    document.addEventListener('keydown', handleEscape, true);

    // Block all editor interactions
    iframeDocument.addEventListener('mousedown', blockEditorEvents, true);
    iframeDocument.addEventListener('mouseup', blockEditorEvents, true);
    iframeDocument.addEventListener('mouseover', blockEditorEvents, true);
    iframeDocument.addEventListener('mouseout', blockEditorEvents, true);

    // Cleanup on unmount
    return cleanup;
  }, [iframeRef, onElementPicked, onCancel]);

  return null;
}

export default ElementPicker;
